import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WhyChooseSection = () => {
  const differentiators = [
    {
      icon: "Users",
      title: "Fractional Executive Model",
      subtitle: "Executive-level expertise without executive-level overhead",
      description: "Access seasoned data strategy executives on a fractional basis, providing strategic guidance and hands-on implementation support tailored to your organization's needs and budget.",
      benefits: [
        "Cost-effective access to senior expertise",
        "Flexible engagement models",
        "Strategic guidance with practical implementation",
        "No long-term executive hiring commitments"
      ],
      comparison: {
        traditional: "Full-time CDO: €200K+ annually",
        northstar: "Fractional expertise: €50K+ annually"
      }
    },
    {
      icon: "GraduationCap",
      title: "Team Training Focus",
      subtitle: "Develop internal capabilities, not external dependencies",
      description: "Our approach emphasizes knowledge transfer and capability building, ensuring your team develops the skills and mindset to sustain and evolve data initiatives independently.",
      benefits: [
        "Comprehensive team skill development",
        "Knowledge transfer, not vendor lock-in",
        "Sustainable internal capabilities",
        "Executive-level thinking at all levels"
      ],
      comparison: {
        traditional: "Vendor dependency for ongoing support",
        northstar: "Self-sufficient, empowered internal teams"
      }
    },
    {
      icon: "Brain",
      title: "Strategic Thinking Methodology",
      subtitle: "Think like a CDO, act with precision",
      description: "We don't just implement solutions—we elevate how your entire organization thinks about data strategy, governance, and value creation through proven frameworks and methodologies.",
      benefits: [
        "Executive-level strategic thinking",
        "Proven proprietary frameworks",
        "Cultural transformation focus",
        "Sustainable competitive advantages"
      ],
      comparison: {
        traditional: "Technology-first implementation",
        northstar: "Strategy-first, technology-enabled transformation"
      }
    },
    {
      icon: "Shield",
      title: "Compliance-First Approach",
      subtitle: "Beyond compliance—toward data maturity",
      description: "Our deep expertise in GDPR, data governance, and privacy regulations ensures your transformation builds competitive advantages while maintaining the highest compliance standards.",
      benefits: [
        "100% regulatory compliance track record",
        "Privacy-by-design implementation",
        "Risk mitigation expertise",
        "Audit-ready documentation"
      ],
      comparison: {
        traditional: "Compliance as afterthought",
        northstar: "Compliance as competitive advantage"
      }
    },
    {
      icon: "Target",
      title: "Measurable Business Impact",
      subtitle: "ROI-focused with transparent metrics",
      description: "Every engagement includes clear success metrics, regular progress tracking, and transparent reporting on business value creation and return on investment.",
      benefits: [
        "Clear ROI measurement frameworks",
        "Regular progress reporting",
        "Business value focus",
        "Transparent success metrics"
      ],
      comparison: {
        traditional: "Vague success criteria",
        northstar: "Measurable business outcomes"
      }
    },
    {
      icon: "Zap",
      title: "Rapid Value Delivery",
      subtitle: "Quick wins while building long-term capabilities",
      description: "Our phased approach delivers immediate value through quick wins while systematically building the foundational capabilities needed for long-term success.",
      benefits: [
        "Immediate value demonstration",
        "Phased implementation approach",
        "Quick wins with strategic foundation",
        "Continuous value delivery"
      ],
      comparison: {
        traditional: "Long implementation cycles",
        northstar: "Rapid value with strategic depth"
      }
    }
  ];

  const trustIndicators = [
       {
      icon: "BookOpen",
      title: "Knowledge Leadership",
      items: [
        "Published data strategy frameworks",
        "Conference speaking engagements",
        "Industry research contributions",
        "Thought leadership articles"
      ]
    },
    {
      icon: "Shield",
      title: "Security & Compliance",
      items: [
        "100% audit success rate",
        "Privacy-by-design methodology",
        "Enterprise security clearances",
        "Regulatory compliance expertise"
      ]
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
              <Icon name="Star" size={24} className="text-secondary" />
            </div>
            <span className="text-secondary font-semibold text-lg">Why Choose Northstar</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            What Makes Us Different
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We're not just another consulting firm. Our unique approach combines executive-level strategic thinking with practical implementation expertise and comprehensive team development.
          </p>
        </div>

        {/* Differentiators Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {differentiators?.map((diff, index) => (
            <div key={index} className="bg-surface rounded-2xl p-8 hover-lift transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-secondary/20">
                  <Icon name={diff?.icon} size={32} className="text-secondary transition-transform duration-300 group-hover:scale-110" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary mb-2">{diff?.title}</h3>
                  <p className="text-secondary font-semibold">{diff?.subtitle}</p>
                </div>
              </div>

              <p className="text-muted-foreground mb-6 leading-relaxed">{diff?.description}</p>

              {/* Benefits */}
              <div className="mb-6">
                <h4 className="font-semibold text-primary mb-3">Key Benefits:</h4>
                <div className="grid sm:grid-cols-2 gap-2">
                  {diff?.benefits?.map((benefit, bIndex) => (
                    <div key={bIndex} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comparison */}
              <div className="bg-white rounded-lg p-4 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-red-600 mb-1">Traditional Approach</div>
                    <div className="text-muted-foreground">{diff?.comparison?.traditional}</div>
                  </div>
                  <div>
                    <div className="font-medium text-secondary mb-1">Northstar Approach</div>
                    <div className="text-muted-foreground">{diff?.comparison?.northstar}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="bg-surface rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-primary mb-4">
              Trust & Credibility Indicators
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our track record speaks for itself. Here's why organizations trust Northstar Analytics with their most critical data transformations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {trustIndicators?.map((indicator, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover-lift transition-all duration-300 hover:scale-105 hover:shadow-xl group">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:bg-primary/20">
                  <Icon name={indicator?.icon} size={32} className="text-primary transition-transform duration-300 group-hover:scale-110" />
                </div>
                <h4 className="text-lg font-bold text-primary mb-4">{indicator?.title}</h4>
                <div className="space-y-2">
                  {indicator?.items?.map((item, iIndex) => (
                    <div key={iIndex} className="flex items-start space-x-2">
                      <Icon name="Check" size={14} className="text-secondary flex-shrink-0 mt-1" />
                      <span className="text-sm text-muted-foreground text-left">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Value Proposition Summary */}
        <div className="mt-16 bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 lg:p-12 text-white text-center">
          <h3 className="text-2xl sm:text-3xl font-bold mb-6">
            The Northstar Advantage
          </h3>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-2xl font-bold mb-2">Strategy First</div>
              <div className="text-white/90">Executive data thinking before tools, platforms, or vendors</div>
            </div>
            <div>
              <div className="text-2xl font-bold mb-2">Agile, Not Heavy</div>
              <div className="text-white/90">Big-consultancy credibility without the drag</div>
            </div>
            <div>
              <div className="text-2xl font-bold mb-2">Built for Endurance</div>
              <div className="text-white/90">Capabilities and culture that outlast the engagement</div>
            </div>
          </div>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            When you choose Northstar Analytics, you're not just hiring consultants—you're partnering with strategic data leaders who are invested in your long-term success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact-multi-channel-engagement-hub">
              <Button
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10 transition-all duration-200 hover:scale-105 hover:shadow-lg focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary active:scale-95"
              >
                Discuss Your Needs
              </Button>
            </Link>
            <Link to="/roi-calculator-suite-value-demonstration-tools">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calculator"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90 transition-all duration-200 hover:scale-105 hover:shadow-lg focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95"
              >
                Calculate Your ROI
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;